import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icons/Icon';
import FormControl from '../FormControl/FormControl.context';
import FormLabel from '../FormLabel/FormLabel';
import PasswordRequirements from '../PasswordRequirements/PasswordRequirements';

import * as Styled from './PasswordInput.styled';

const passwordValidations = [
  { name: 'validLength', test: (v) => v.length >= 8 },
  { name: 'hasCase', test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) },
  { name: 'hasNumber', test: (v) => /\d/.test(v) },
  {
    name: 'hasSpecial',
    test: (v) => /[{}()[\]<>|#?!@$%^&*~_=+;:'".,\\-]/.test(v)
  }
];

/**
 * `PasswordInput` allows a user to insert a password into a UI.
 * It gives the user the option to hide or show the password text.
 * It comes with a label, input, and error message support.
 * This component is meant for implementation in a Formik form.
 */
const PasswordInput = ({
  id,
  name,
  validate,
  innerRef,
  label,
  className,
  withRequirements,
  ...inputProps
}) => {
  const [showPassword, setType] = useState(null);
  const [validations, setValidations] = useState({});

  const validatePassword = (value) => {
    let hasError = false;
    setValidations(
      passwordValidations.reduce((acc, { name: validationName, test }) => {
        acc[validationName] = test(value);
        if (!acc[validationName]) hasError = true;
        return acc;
      }, {})
    );
    return hasError;
  };

  return (
    <FormControl
      id={id}
      name={name}
      validate={withRequirements ? validatePassword : validate}
      containerClass={className}
    >
      {({ hasActivity }) => (
        <>
          <FormLabel htmlFor={id} disabled={inputProps.disabled}>
            {label}
          </FormLabel>
          <Styled.Input
            id={id}
            ref={innerRef}
            type={showPassword ? 'text' : 'password'}
            {...inputProps}
          />
          {withRequirements && hasActivity && (
            <PasswordRequirements {...validations} />
          )}

          <Styled.IconContainer>
            <Icon
              data-testid="icon"
              onClick={() => setType(!showPassword)}
              name={showPassword ? 'hide' : 'show'}
            />
          </Styled.IconContainer>
        </>
      )}
    </FormControl>
  );
};

PasswordInput.propTypes = {
  /**
   * CSS class name
   */
  className: PropTypes.string,
  /**
   * Form ID use to associate the label and input
   * Use specific naming, as a collision can cause undesirable effects.
   */
  id: PropTypes.string.isRequired,
  /**
   * Inner ref; allowing you to interact directly with the Input element.
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(PropTypes.element) })
  ]),
  /**
   * Formik field name
   */
  name: PropTypes.string.isRequired,
  /**
   * Formik validation function
   * This is ignored when rendering `withRequirements`.
   */
  validate: PropTypes.func,
  /**
   * Form label
   */
  label: PropTypes.string.isRequired,
  /**
   * If `true`, uses built-in validation and displays password requirement criteria.
   * When in use, omit the field from your Formik form's schema.
   */
  withRequirements: PropTypes.bool
};

PasswordInput.defaultProps = {
  className: null,
  innerRef: undefined,
  validate: undefined,
  withRequirements: false
};

export default PasswordInput;
